import React from 'react';
import { Routes, Route, Link, Navigate, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useOrgStore } from '../stores/orgStore';
import { UserMenu } from '../components/Layout/UserMenu';
import { MembersManager } from '../components/Settings/MembersManager';
import { DesignSystem } from './admin/DesignSystem';
import { OrgsList } from './admin/OrgsList';
import { UsersList } from './admin/UsersList';
import { AdminDashboard } from './admin/AdminDashboard';

import { LLMUsage } from './admin/LLMUsage';
import { LLMLogs } from './admin/LLMLogs';
import { JobsDashboard } from './admin/JobsDashboard';
import { JobsList } from './admin/JobsList';
import { ScheduledJobs } from './admin/ScheduledJobs';
import { AdminSettings } from './admin/AdminSettings';
import { SidebarLayout } from '../components/Layout/SidebarLayout';
import { OrgOverview } from './organization/OrgOverview';
import { MembersAndInvites } from './organization/MembersAndInvites';
import { RolesAndPermissions } from './organization/RolesAndPermissions';
import { OrgAttachments } from './organization/OrgAttachments';
// AboutCompany and CompanyKnowledge removed — replaced by ROROS overarching content (/roros/company-profile)
import { BillingSettings } from './organization/BillingSettings';
import { GeneralSettings } from './organization/GeneralSettings';
import { DashboardPage } from './dashboard/DashboardPage';
// Onboarding removed — product-specific onboarding handled by route guards (e.g., RorosGuard → Launchpad)
import { ProfilePage } from './settings/Account/ProfilePage';
import { SecurityPage } from './settings/Account/SecurityPage';
import { SessionsPage } from './settings/Account/SessionsPage';
import { DangerPage } from './settings/Account/DangerPage';
import { ExportPage } from './settings/Account/ExportPage';
import { Documentation } from './organization/Documentation';
import { TranslationsAdmin } from './admin/TranslationsAdmin';
import { TranslationOverrides } from './organization/TranslationOverrides';
import { ThemeSettings } from './organization/ThemeSettings';
import { AISettings } from './organization/AISettings';
import { ChatPage } from './chat/ChatPage';
import { Forbidden } from './Forbidden';
import { PermissionProtectedRoute } from '@/components/routing/PermissionProtectedRoute';
import { PERMISSIONS } from '@/constants/permissions';
import { appConfig, HOME_ROUTE } from '@/config/app';
import { RorosLaunchpad } from './roros/Launchpad';
import { RorosGuard } from './roros/RorosGuard';
import { RorosSidebarLayout } from '@/components/roros/RorosSidebarLayout';
// F5: LearningItemsPage replaces the placeholder route.
import { LearningItemsPage } from './roros/learning/LearningItemsPage';
import { RorosPage } from '@/components/roros/RorosPage';
// Side effect: registers 'roros' layout config with the boilerplate registry.
// Must be imported before any <SidebarLayout type="roros"> renders.
import '@/routes/roros/layout.config';
import { ControlTowerPage } from './roros/control-tower/ControlTowerPage';
import {
  OverarchingDashboard,
  PersonasPage,
  PersonaDetailPage,
} from './roros/overarching';
import { SystemSettingsPage as RorosSystemSettingsPage } from './roros/settings/SystemSettingsPage';
import { StructureSettingsPage as RorosStructureSettingsPage } from './roros/settings/StructureSettingsPage';
import { ConnectionsPage as RorosConnectionsPage } from './roros/settings/ConnectionsPage';
import { DomainsPage, DomainDetailPage } from './roros/domains';
// Wave E: ActorDetailPage removed — detail route replaced by Navigate redirect below.
import { ActorsPage } from './roros/actors';
// F10 dogfood-fix-wave-1: AiOperatorDetailPage removed — detail view consolidated into editor modal.
import { AiOperatorsPage } from './roros/ai-operators';
// F-SKILL-1 Packet B1 — Skills library + create/import (AC-SKILL-01..05).
import { SkillsPage } from './roros/skills';
// J7-W2 — Role Contract full editor (PRD §12.3.10 task 2) + Budget Ledger (task 5).
import {
  RoleContractsPage,
  RoleContractDetailPage,
  RoleContractEditor,
} from './roros/role-contracts';
import { BudgetLedgerPage } from './roros/governance/BudgetLedgerPage';
// J7-W3 (PRD §12.3.10 task 3) — Delegations + Persona Consultations.
import { DelegationsPage, DelegationDetailPage } from './roros/delegations';
import { ConsultationsPage, ConsultationDetailPage } from './roros/consultations';
// Agent Sessions M1 Packet T — agentic chat (AC-AGS-4). M4: list page added.
import { AgentChatPage, AgentSessionsListPage } from './roros/agent-sessions';
import { GoalsPage } from './roros/artifacts/GoalsPage';
import { TreeViewPage } from './roros/artifacts/TreeViewPage';
import { OutcomesPage } from './roros/artifacts/OutcomesPage';
import { MetricsPage } from './roros/artifacts/MetricsPage';
import { KnowledgeItemsPage } from './roros/artifacts/KnowledgeItemsPage';
// J7-W4 — Knowledge full editor (PRD §12.3.10 task 6a).
import { KnowledgeDetailPage } from './roros/artifacts/KnowledgeDetailPage';
// Ticket 6941e519 (2026-06-04): standalone Principles page removed — principles
// live in the Constitution tab of Structure Settings. PrinciplesPage import dropped.
import { RoutinesPage } from './roros/artifacts/RoutinesPage';
import { SourceOfTruthPage } from './roros/governance/SourceOfTruthPage';
// TB-1 (AC-DM-09): DecisionRightsPage replaced by a redirect in AppRouter.
// The panel logic is now in src/routes/roros/settings/panels/DecisionMakingPanel.tsx.
// J7-W6 — Exploration Zones admin (PRD §12.3.10 task 8 / §9.12.3).
// TB-1 (AC-DM-10): Exploration Zones routes remain registered (dormant — no nav entry).
import { ExplorationZonesPage } from './roros/governance/ExplorationZonesPage';
import { ExplorationZoneDetailPage } from './roros/governance/ExplorationZoneDetailPage';
// J7-W7 — Intake Sources admin (PRD §12.3.10 task 7, §9.6.5).
// IntakeSourcesPage is re-exported from IntakeSourcesPanel — the standalone
// route (/roros/intake/sources) now redirects to Structure Settings (AC-OPSNAV-3).
// The import is kept here to prevent orphan-file lint issues; do not use in routes.
// J5 — Week-1 Cadence pages
// M-Rhythm-1: CadencePage deleted; /roros/cadence → 301 redirect to /roros/rhythm/calendar
// import { CadencePage } from './roros/cadence/CadencePage'; // DELETED in M-Rhythm-1
import { WeeklyReviewPage } from './roros/cadence/WeeklyReviewPage';
// M-Rhythm-2: CalendarPlaceholderPage replaced by full CalendarPage (kept in codebase, route replaced in Wave 4C)
// import { CalendarPage } from './roros/rhythm/CalendarPage'; // Wave 4C: superseded by MeetingsHubPage
// Wave 4C: Unified Calendar + Meetings hub
import { MeetingsHubPage } from './roros/rhythm/MeetingsHubPage';
// F-Meeting-1 Wave 2: Meeting facilitation wizard
import { MeetingWizardPage } from './roros/meetings/MeetingWizardPage';
// AC-FW2-FU1-1: Meetings list page (kept in codebase; route redirects to hub in Wave 4C)
// import { MeetingsListPage } from './roros/meetings/MeetingsListPage'; // Wave 4C: replaced by redirect
// AC-FW3a-4-1: AnnualOutcomesPage deleted — features merged into OutcomesPage.
// /roros/outcomes/annual now redirects to /roros/artifacts/outcomes?horizon=annual.
// VerificationInboxPage removed from router — AC-inbox-15 (old path redirects to /roros/inbox)
// CascadeInboxPage removed — AC-inbox-16 (replaced by unified InboxPage + redirect)
import { InboxPage } from './roros/inbox/InboxPage';
import { DecisionLogPage } from './roros/decisions/DecisionLogPage';
// J6 — Growth Mode Add-Ons + Road-Metaphor Dashboards
import { InitiativesPage } from './roros/artifacts/InitiativesPage';
import { ScorecardPage } from './roros/dashboards/ScorecardPage';
import { RoadDashboardPage } from './roros/dashboards/RoadDashboardPage';
import { useOperatingMode } from '@/hooks/roros/useOperatingMode';

/**
 * RorosHomeRoute — unified home dashboard.
 *
 * v1.1.17: previously the CEO persona (anyone with
 * `roros.operating-mode.change`) saw the Road Dashboard and everyone else saw
 * the J5 Operating Pulse home — two completely different pages on the same
 * route depending on permissions. Unified to the Road Dashboard for everyone.
 * Sub-sections that read data the user can't access will gracefully render
 * empty states; nothing in the Road Dashboard requires write permissions.
 */
function RorosHomeRoute() {
  return <RoadDashboardPage />;
}

/**
 * IntakeSourcesRedirect — AC-OPSNAV-3
 *
 * Redirects the old /roros/intake/sources URL to the new Structure Settings
 * Intake Sources tab, preserving the ?edit-intake=<id> query param so that
 * existing deep-links still open the edit sheet.
 *
 * Example:
 *   /roros/intake/sources               → /roros/structure-settings?tab=intake-sources
 *   /roros/intake/sources?edit-intake=x → /roros/structure-settings?tab=intake-sources&edit-intake=x
 */
function IntakeSourcesRedirect() {
  const [searchParams] = useSearchParams();
  const editIntake = searchParams.get('edit-intake');
  const target = editIntake
    ? `/roros/structure-settings?tab=intake-sources&edit-intake=${encodeURIComponent(editIntake)}`
    : '/roros/structure-settings?tab=intake-sources';
  return <Navigate to={target} replace />;
}

/**
 * GrowthGate — J6 nav gate
 *
 * Wraps a page so it 404s in Founder mode but renders normally in Growth/Scaling.
 * Used for Initiatives Portfolio and Cadence Scorecard surfaces.
 */
function GrowthGate({ children }: { children: React.ReactNode }) {
  const { isAtLeastGrowth, isLoading } = useOperatingMode();
  if (isLoading) return null;
  if (!isAtLeastGrowth) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
        <h1 className="text-2xl font-bold text-foreground mb-2">Growth-mode feature</h1>
        <p className="text-muted-foreground max-w-md">
          This page is available once your operating mode is set to Growth or Scaling.
          Switch modes from Structure Settings → Operating Mode.
        </p>
      </div>
    );
  }
  return <>{children}</>;
}

/** Placeholder shown for all ROROS pages during frontend rebuild */
function RorosUnderConstruction() {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
      <div className="text-6xl mb-4">🚧</div>
      <h1 className="text-2xl font-bold text-foreground mb-2">Under Construction</h1>
      <p className="text-muted-foreground max-w-md">
        This page is being rebuilt with improved quality. Check back soon.
      </p>
    </div>
  );
}

function RorosPlaceholder({ title, icon }: { title: string; icon?: React.ReactNode }) {
  // RR-0003: Wrap in RorosPage so the page-header chrome shows the title (and
  // an optional icon — e.g. Lightbulb for Learning). Without this, placeholder
  // routes had a blank header centre.
  return (
    <RorosPage title={title} icon={icon}>
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-8">
        <h1 className="text-2xl font-bold text-foreground mb-2">Coming Soon: {title}</h1>
        <p className="text-muted-foreground max-w-md">
          This page is being built. Check back soon.
        </p>
      </div>
    </RorosPage>
  );
}

export function AppRouter() {
  return (
    <>
      <Routes>
        <Route path="/dashboard" element={<Navigate to={HOME_ROUTE} replace />} />
        <Route path="/attachments" element={<Navigate to={HOME_ROUTE} replace />} />
        <Route path="/admin" element={<Navigate to="/admin/orgs" replace />} />
        <Route path="/admin/orgs" element={<SidebarLayout type="admin"><OrgsList /></SidebarLayout>} />
        <Route path="/admin/orgs/:id" element={<AdminOrgRedirect />} />
        <Route path="/admin/users" element={<SidebarLayout type="admin"><UsersList /></SidebarLayout>} />
        {/* Invitations route removed - invite data merged into Users page */}
        <Route path="/admin/design-system" element={<SidebarLayout type="admin"><DesignSystem /></SidebarLayout>} />
        <Route path="/admin/llm/usage" element={<SidebarLayout type="admin"><LLMUsage /></SidebarLayout>} />
        <Route path="/admin/llm/logs" element={<SidebarLayout type="admin"><LLMLogs /></SidebarLayout>} />
        <Route path="/admin/jobs" element={<SidebarLayout type="admin"><JobsDashboard /></SidebarLayout>} />
        <Route path="/admin/jobs/list" element={<SidebarLayout type="admin"><JobsList /></SidebarLayout>} />
        <Route path="/admin/scheduled-jobs" element={<SidebarLayout type="admin"><ScheduledJobs /></SidebarLayout>} />
        <Route path="/admin/settings" element={<SidebarLayout type="admin"><AdminSettings /></SidebarLayout>} />
        <Route path="/admin/translations" element={<SidebarLayout type="admin"><TranslationsAdmin /></SidebarLayout>} />
        <Route path="/admin/documentation" element={<SidebarLayout type="admin"><Documentation /></SidebarLayout>} />
        <Route path="/organization" element={<SidebarLayout type="organization"><OrgOverview /></SidebarLayout>} />
        <Route path="/organization/members" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              anyPermissions={[PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_USERS]}
              deniedMessage="You need permission to view members and invites"
            >
              <MembersAndInvites />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/organization/settings/roles" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ROLES}
              deniedMessage="You need permission to manage roles and permissions"
            >
              <RolesAndPermissions />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        <Route path="/forbidden" element={<SidebarLayout type="organization"><Forbidden /></SidebarLayout>} />
        <Route path="/organization/attachments" element={
          <SidebarLayout type="organization">
            <PermissionProtectedRoute 
              permission={PERMISSIONS.MANAGE_ATTACHMENTS}
              deniedMessage="You need permission to manage attachments"
            >
              <OrgAttachments />
            </PermissionProtectedRoute>
          </SidebarLayout>
        } />
        {/* Company Knowledge and Organization (About) routes removed — replaced by ROROS overarching content */}
        {/* Organization settings routes - require MANAGE_ORGANIZATION_SETTINGS */}
        {/* AI settings will be rebuilt in Phase 5 (Per-Tenant AI/LLM Configuration) */}
        <Route path="/organization/billing" element={<SidebarLayout type="organization"><BillingSettings /></SidebarLayout>} />
        <Route path="/organization/settings" element={<SidebarLayout type="organization"><GeneralSettings /></SidebarLayout>} />
        <Route path="/organization/documentation" element={<SidebarLayout type="organization"><Documentation /></SidebarLayout>} />
        <Route path="/organization/translations" element={<SidebarLayout type="organization"><TranslationOverrides /></SidebarLayout>} />
        <Route path="/organization/theme" element={<SidebarLayout type="organization"><ThemeSettings /></SidebarLayout>} />
        <Route path="/organization/ai-settings" element={<SidebarLayout type="organization"><AISettings /></SidebarLayout>} />
        <Route path="/chat" element={<SidebarLayout type="organization" fullHeight><ChatPage /></SidebarLayout>} />
        {/* Boilerplate onboarding removed — product-specific onboarding via route guards */}
        {/* Content plan routes removed in Milestone 7 */}
        {/* Background generator route removed in Milestone 3 */}
        <Route path="/settings/account" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/profile" element={<SidebarLayout type="account"><ProfilePage /></SidebarLayout>} />
        <Route path="/settings/account/security" element={<SidebarLayout type="account"><SecurityPage /></SidebarLayout>} />
        <Route path="/settings/account/sessions" element={<SidebarLayout type="account"><SessionsPage /></SidebarLayout>} />
        <Route path="/settings/account/danger" element={<SidebarLayout type="account"><DangerPage /></SidebarLayout>} />
        <Route path="/settings/account/export" element={<SidebarLayout type="account"><ExportPage /></SidebarLayout>} />
        <Route path="/settings/members" element={<SidebarLayout type="organization"><MembersManager /></SidebarLayout>} />
        {/* /accept-invite is now handled in public router (main.tsx) */}
        {/* ─── ROROS Routes ─────────────────────────────────────────── */}
        {/* Note: All /roros/* routes use RorosSidebarLayout (not SidebarLayout type="roros")
            because RorosSidebarLayout injects the dynamic "Open work" badge on Control Tower. */}
        <Route path="/roros/launchpad" element={<RorosLaunchpad />} />
        {/* Root route — J6: CEO sees Road Dashboard, other roles keep Operating Pulse */}
        <Route path="/" element={<RorosGuard><RorosSidebarLayout><RorosHomeRoute /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros" element={<RorosGuard><RorosSidebarLayout><RorosHomeRoute /></RorosSidebarLayout></RorosGuard>} />
        {/* J6 — explicit Road Dashboard + Cadence Scorecard routes */}
        <Route path="/roros/dashboards/road" element={<RorosGuard><RorosSidebarLayout><RoadDashboardPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/dashboards/scorecard" element={<RorosGuard><RorosSidebarLayout><GrowthGate><ScorecardPage /></GrowthGate></RorosSidebarLayout></RorosGuard>} />
        {/* J3-A: Control Tower queue (replaces placeholder) */}
        <Route path="/roros/control-tower" element={<RorosGuard><RorosSidebarLayout fullHeight><ControlTowerPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/work-items" element={<RorosGuard><RorosSidebarLayout><RorosPlaceholder title="Work Items" /></RorosSidebarLayout></RorosGuard>} />
        {/* Unified Inbox Packet 1 — AC-inbox-1/14 */}
        <Route path="/roros/inbox" element={<RorosGuard><RorosSidebarLayout><InboxPage /></RorosSidebarLayout></RorosGuard>} />
        {/* AC-inbox-15: old paths redirect to /roros/inbox */}
        <Route path="/roros/verification-inbox" element={<Navigate to="/roros/inbox" replace />} />
        {/* AC-inbox-15/16: cascade-inbox redirects; CascadeInboxPage removed */}
        <Route path="/roros/cascade-inbox" element={<Navigate to="/roros/inbox" replace />} />
        <Route path="/roros/domains" element={<RorosGuard><RorosSidebarLayout><DomainsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/domains/:id" element={<RorosGuard><RorosSidebarLayout><DomainDetailPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/actors" element={<RorosGuard><RorosSidebarLayout><ActorsPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Wave E AC-EDIT-4: detail page removed — old /roros/actors/:id URLs redirect gracefully */}
        <Route path="/roros/actors/:id" element={<Navigate to="/roros/actors" replace />} />
        {/* Wave H (AC-NAV-2): /roros/ai-operators → /roros/actors redirect.
            The "AI Operators" nav entry is now "Actors" → /roros/actors.
            Deep-links to /roros/ai-operators land on the unified Actors page. */}
        <Route path="/roros/ai-operators" element={<Navigate to="/roros/actors" replace />} />
        {/* F10 dogfood-fix-wave-1: /roros/ai-operators/:actorId removed — view+edit consolidated into the list page editor modal. */}
        {/* F-SKILL-1 Packet B1 — Skills library (AC-SKILL-01..05). */}
        <Route path="/roros/skills" element={<RorosGuard><RorosSidebarLayout><SkillsPage /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W2 — Role Contract full editor (PRD §12.3.10 task 2). */}
        <Route path="/roros/role-contracts" element={<RorosGuard><RorosSidebarLayout><RoleContractsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/role-contracts/new" element={<RorosGuard><RorosSidebarLayout><RoleContractEditor /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/role-contracts/:id" element={<RorosGuard><RorosSidebarLayout><RoleContractDetailPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/role-contracts/:id/edit" element={<RorosGuard><RorosSidebarLayout><RoleContractEditor /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W2 — Budget Ledger viewer (PRD §12.3.10 task 5). */}
        <Route path="/roros/governance/budget-ledger" element={<RorosGuard><RorosSidebarLayout><BudgetLedgerPage /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W3 — Delegations (PRD §12.3.10 task 3a). */}
        <Route path="/roros/delegations" element={<RorosGuard><RorosSidebarLayout><DelegationsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/delegations/new" element={<RorosGuard><RorosSidebarLayout><DelegationsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/delegations/:id" element={<RorosGuard><RorosSidebarLayout><DelegationDetailPage /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W3 — Persona Consultations (PRD §12.3.10 task 3b/3c). */}
        <Route path="/roros/consultations" element={<RorosGuard><RorosSidebarLayout><ConsultationsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/consultations/:id" element={<RorosGuard><RorosSidebarLayout><ConsultationDetailPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Agent Sessions M4: /roros/agent-sessions = list (history), /roros/agent-sessions/new = new session, /roros/agent-sessions/:id = chat detail */}
        <Route path="/roros/agent-sessions" element={<RorosGuard><RorosSidebarLayout><AgentSessionsListPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/agent-sessions/new" element={<RorosGuard><RorosSidebarLayout fullHeight><AgentChatPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/agent-sessions/:id" element={<RorosGuard><RorosSidebarLayout fullHeight><AgentChatPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/goals" element={<RorosGuard><RorosSidebarLayout><GoalsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/outcomes" element={<RorosGuard><RorosSidebarLayout><OutcomesPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Wave B — Strategic Tree view */}
        <Route path="/roros/artifacts/tree" element={<RorosGuard><RorosSidebarLayout><TreeViewPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/metrics" element={<RorosGuard><RorosSidebarLayout><MetricsPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Ticket 6941e519 (2026-06-04): standalone Principles page removed —
            principles are managed in the Constitution tab of Structure Settings.
            Old /roros/artifacts/principles[/:id] URLs redirect there for back-compat. */}
        <Route path="/roros/artifacts/principles" element={<Navigate to="/roros/structure-settings?edit-constitution=1" replace />} />
        <Route path="/roros/artifacts/principles/:id" element={<Navigate to="/roros/structure-settings?edit-constitution=1" replace />} />
        <Route path="/roros/artifacts/routines" element={<RorosGuard><RorosSidebarLayout><RoutinesPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Dogfood-fix-wave-1 F4: GrowthGate removed — Initiatives visible to all Founders from day one. */}
        <Route path="/roros/artifacts/initiatives" element={<RorosGuard><RorosSidebarLayout><InitiativesPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/knowledge" element={<RorosGuard><RorosSidebarLayout><KnowledgeItemsPage /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W4 task 6a — Knowledge full editor (detail + edit alias). */}
        <Route path="/roros/artifacts/knowledge/:id" element={<RorosGuard><RorosSidebarLayout><KnowledgeDetailPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/knowledge/:id/edit" element={<RorosGuard><RorosSidebarLayout><KnowledgeDetailPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/artifacts/learning" element={<RorosGuard><RorosSidebarLayout><LearningItemsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/decisions" element={<RorosGuard><RorosSidebarLayout><DecisionLogPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/quality" element={<RorosGuard><RorosSidebarLayout><RorosPlaceholder title="Quality" /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/routines" element={<RorosGuard><RorosSidebarLayout><RorosPlaceholder title="Routines" /></RorosSidebarLayout></RorosGuard>} />
        {/* M-Rhythm-1: /roros/cadence removed → 301 redirect to /roros/rhythm/calendar for graceful link handling */}
        <Route path="/roros/cadence" element={<Navigate to="/roros/rhythm/calendar" replace />} />
        {/* M-Rhythm-1: /roros/cadence/weekly-review → redirect alias for 2-week backwards compat */}
        <Route path="/roros/cadence/weekly-review" element={<Navigate to="/roros/rhythm/weekly-review" replace />} />
        {/* M-Rhythm-1: New Rhythm routes */}
        {/* Wave 4C: /roros/rhythm/calendar now renders the unified MeetingsHubPage (Calendar + List tabs). */}
        <Route path="/roros/rhythm/calendar" element={<RorosGuard><RorosSidebarLayout><MeetingsHubPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/rhythm/weekly-review" element={<RorosGuard><RorosSidebarLayout><WeeklyReviewPage /></RorosSidebarLayout></RorosGuard>} />
        {/* AC-4C-1: /roros/meetings 301-redirects to unified page's List tab. MUST be before /:id/run. */}
        <Route path="/roros/meetings" element={<Navigate to="/roros/rhythm/calendar?view=list" replace />} />
        {/* F-Meeting-1 Wave 2: Meeting facilitation wizard (full-screen, no sidebar) */}
        <Route path="/roros/meetings/:id/run" element={<RorosGuard><MeetingWizardPage /></RorosGuard>} />
        {/* AC-FW3a-4-1: /roros/outcomes/annual → redirect to unified OutcomesPage with
            horizon pre-filter. Back-compat alias; remove after 2 weeks. */}
        <Route path="/roros/outcomes/annual" element={<Navigate to="/roros/artifacts/outcomes?horizon=annual" replace />} />
        <Route path="/roros/governance/source-of-truth" element={<RorosGuard><RorosSidebarLayout><SourceOfTruthPage /></RorosSidebarLayout></RorosGuard>} />
        {/* J7-W6 — Exploration Zones admin (PRD §12.3.10 task 8 / §9.12.3). */}
        <Route path="/roros/governance/exploration-zones" element={<RorosGuard><RorosSidebarLayout><ExplorationZonesPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/governance/exploration-zones/:id" element={<RorosGuard><RorosSidebarLayout><ExplorationZoneDetailPage /></RorosSidebarLayout></RorosGuard>} />
        {/* AC-OPSNAV-3: /roros/intake/sources → redirect to Structure Settings Intake Sources tab.
            Preserves ?edit-intake=<id> so existing deep-links still open the edit sheet.
            IntakeSourcesPage import is still used by the panel; the standalone route is gone. */}
        <Route path="/roros/intake/sources" element={<IntakeSourcesRedirect />} />
        {/* TB-1 (AC-DM-09): /roros/governance → redirect to Structure Settings Decision Making tab. */}
        <Route path="/roros/governance" element={<Navigate to="/roros/structure-settings?tab=decision-making" replace />} />
        <Route path="/roros/observability" element={<RorosGuard><RorosSidebarLayout><RorosPlaceholder title="Observability" /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/company-profile" element={<RorosGuard><RorosSidebarLayout><OverarchingDashboard /></RorosSidebarLayout></RorosGuard>} />
        {/* Personas catalog is retained as a library view accessible from inside the Market tab. */}
        <Route path="/roros/company-profile/personas" element={<RorosGuard><RorosSidebarLayout><PersonasPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/company-profile/personas/:id" element={<RorosGuard><RorosSidebarLayout><PersonaDetailPage /></RorosSidebarLayout></RorosGuard>} />
        {/* Deep-link redirects for removed sub-pages — land users on the correct dashboard tab. */}
        <Route path="/roros/company-profile/edit" element={<Navigate to="/roros/company-profile?tab=company" replace />} />
        <Route path="/roros/company-profile/core-values" element={<Navigate to="/roros/company-profile?tab=company" replace />} />
        <Route path="/roros/company-profile/company-principles" element={<Navigate to="/roros/company-profile?tab=company" replace />} />
        <Route path="/roros/company-profile/usps" element={<Navigate to="/roros/company-profile?tab=market" replace />} />
        <Route path="/roros/company-profile/target-markets" element={<Navigate to="/roros/company-profile?tab=market" replace />} />
        <Route path="/roros/company-profile/target-markets/:id" element={<Navigate to="/roros/company-profile?tab=market" replace />} />
        <Route path="/roros/company-profile/swt-analysis" element={<Navigate to="/roros/company-profile?tab=strategy" replace />} />
        <Route path="/roros/company-profile/swt" element={<Navigate to="/roros/company-profile?tab=strategy" replace />} />
        <Route path="/roros/company-profile/icps" element={<Navigate to="/roros/company-profile?tab=market" replace />} />
        <Route path="/roros/company-profile/icps/:id" element={<Navigate to="/roros/company-profile?tab=market" replace />} />
        <Route path="/roros/settings" element={<RorosGuard><RorosSidebarLayout><RorosSystemSettingsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/structure-settings" element={<RorosGuard><RorosSidebarLayout><RorosStructureSettingsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/settings/connections" element={<RorosGuard><RorosSidebarLayout><RorosConnectionsPage /></RorosSidebarLayout></RorosGuard>} />
        <Route path="/roros/*" element={<RorosGuard><RorosSidebarLayout><RorosUnderConstruction /></RorosSidebarLayout></RorosGuard>} />
        <Route path="*" element={<Navigate to={HOME_ROUTE} replace />} />
      </Routes>
    </>
  );
}

function DashboardWithHeader() {
  return (
    <div className="bg-page-bg min-h-screen">
      <div className="h-16 bg-background border-b flex items-center justify-between px-6">
        <Link to="/organization" className="flex items-center hover:opacity-80 transition-opacity">
          <img 
            src="/images/logo.png"
            alt={appConfig.name} 
            className="h-6 object-contain"
          />
        </Link>
        <UserMenu />
      </div>
      <DashboardPage />
    </div>
  );
}

/** Redirect /admin/orgs/:id → set activeOrgId and navigate to /organization */
function AdminOrgRedirect() {
  const { id } = useParams();
  const navigate = useNavigate();
  const setActiveOrgId = useOrgStore(s => s.setActiveOrgId);

  React.useEffect(() => {
    if (id) {
      setActiveOrgId(id);
      navigate('/organization', { replace: true });
    }
  }, [id, setActiveOrgId, navigate]);

  return null;
}

// AdminPageWithHeader removed in v1.1.4 — /settings/members migrated to SidebarLayout
